import { unknownEntries, isUnknownArray, isRecord } from "../types/data";
import { markNotificationsLogStale } from "./activityFeedFreshness";
import type { DateValue } from "../types/data";
import type { ExperienceEvent, ExperienceEventInput } from "../types/experience";
import type firebase from "firebase/compat/app";
import "firebase/compat/firestore";

const sanitizeValue = (value: unknown): unknown => {
  if (value === undefined) return undefined;
  if (value === null || value instanceof Date) return value;
  if (isRecord(value) && typeof value.toDate === "function" && typeof value.seconds === "number") {
    return value;
  }
  if (isUnknownArray(value)) {
    return value.map(sanitizeValue).filter((item) => item !== undefined);
  }
  if (typeof value === "object") {
    return unknownEntries(value).reduce<Record<string, unknown>>((result, [key, entry]) => {
      const normalized = sanitizeValue(entry);
      if (normalized !== undefined) result[key] = normalized;
      return result;
    }, {});
  }
  return value;
};

export const getExperienceNotificationDocumentId = (key: string) =>
  String(key || "")
    .replace(/[^a-zA-Zа-яА-Я0-9_-]/g, "_")
    .slice(0, 120);

export const normalizeExperienceEvent = (event: unknown = {}): ExperienceEvent | null => {
  if (!isRecord(event)) return null;
  const key = String(event.key || event.dedupeKey || "").trim();
  const experience = Number(event.experience);
  if (!key || !Number.isFinite(experience) || experience === 0) return null;

  const metadata: Record<string, unknown> = {};
  Object.entries(isRecord(event.metadata) ? event.metadata : {}).forEach(([key, value]) => {
    const normalized = sanitizeValue(value);
    if (normalized !== undefined) metadata[key] = normalized;
  });
  return {
    key,
    scope: String(event.scope || "own"),
    type: String(event.type || "experience"),
    category: String(event.category || "progress_experience"),
    mode: String(event.mode || "toast"),
    title: String(event.title || `${experience > 0 ? "+" : ""}${experience} опыта`),
    description: String(event.description || ""),
    experience,
    metadata
  };
};

// This function never changes XP. It only writes the durable explanation in
// the same transaction that has already decided the applied XP delta.
export const writeExperienceNotification = ({
  transaction,
  firestore,
  userId,
  event,
  createdAt
}: { transaction?: firebase.firestore.Transaction | null; firestore?: firebase.firestore.Firestore | null; userId?: string | null; event?: ExperienceEventInput | null; createdAt?: DateValue }) => {
  const normalized = normalizeExperienceEvent(event);
  const normalizedUserId = String(userId || "").trim();
  if (!transaction || !firestore || !normalizedUserId || !normalized) return null;

  const reference = firestore
    .collection("userNotifications")
    .doc(normalizedUserId)
    .collection("notifications")
    .doc(getExperienceNotificationDocumentId(normalized.key));
  transaction.set(reference, {
    key: normalized.key,
    scope: normalized.scope,
    type: normalized.type,
    category: normalized.category,
    title: normalized.title,
    description: normalized.description,
    experience: normalized.experience,
    metadata: normalized.metadata,
    read: false,
    createdAt: createdAt || new Date()
  });
  return normalized;
};

export const dispatchExperienceEvents = (events: ExperienceEventInput | ExperienceEventInput[] = [], userId = "") => {
  const normalizedEvents = (isUnknownArray(events) ? events : [events])
    .map(normalizeExperienceEvent)
    .filter((event): event is ExperienceEvent => event !== null);
  if (normalizedEvents.length && userId) {
    markNotificationsLogStale(userId, "owner");
  }
  if (typeof window === "undefined") return normalizedEvents;
  normalizedEvents.forEach((event) => {
    window.dispatchEvent(new CustomEvent("movie-celebration", {
      detail: event
    }));
  });
  return normalizedEvents;
};
